import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { getCompanySettings } from './services/api'
import { setCompanyStateCode } from './lib/gst'
import Layout from './components/Layout'
import AuthRoute from './components/AuthRoute'
import { useAuth } from './contexts/AuthContext'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Invoices from './pages/Invoices'
import InvoiceForm from './pages/InvoiceForm'
import InvoiceDetail from './pages/InvoiceDetail'
import Estimates from './pages/Estimates'
import EstimateForm from './pages/EstimateForm'
import EstimateDetail from './pages/EstimateDetail'
import DeliveryChallans from './pages/DeliveryChallans'
import DeliveryChallanForm from './pages/DeliveryChallanForm'
import DeliveryChallanDetail from './pages/DeliveryChallanDetail'
import CreditNotes from './pages/CreditNotes'
import CreditNoteForm from './pages/CreditNoteForm'
import CreditNoteDetail from './pages/CreditNoteDetail'
import Customers from './pages/Customers'
import CustomerForm from './pages/CustomerForm'
import CustomerDetail from './pages/CustomerDetail'
import Products from './pages/Products'
import ProductForm from './pages/ProductForm'
import Payments from './pages/Payments'
import Expenses from './pages/Expenses'
import ExpenseForm from './pages/ExpenseForm'
import ExpenseDetail from './pages/ExpenseDetail'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import BankReconciliation from './pages/BankReconciliation'
import StaffDashboard from './pages/StaffDashboard'
import CreatorHome from './pages/CreatorHome'

function AppRoutes() {
  const { isAdmin, isStaff, isInvoiceCreator } = useAuth()

  // Seed the seller's GST state from company settings so the live tax preview in the
  // invoice/estimate forms matches what the backend will store. Falls back to 29.
  useEffect(() => {
    getCompanySettings()
      .then((res) => setCompanyStateCode(res.data?.stateCode))
      .catch(() => { /* keep the fallback; the backend is authoritative on save */ })
  }, [])

  if (isInvoiceCreator) {
    // INVOICE_CREATOR: raise invoices + download their PDFs only. No financials.
    return (
      <Routes>
        <Route path="/" element={<CreatorHome />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/new" element={<InvoiceForm />} />
        <Route path="/invoices/:id" element={<InvoiceDetail />} />
        <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
        <Route path="/customers/new" element={<CustomerForm />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  if (isStaff) {
    // STAFF: limited routes — only expenses + own invoices
    return (
      <Routes>
        <Route path="/" element={<StaffDashboard />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/expenses/new" element={<ExpenseForm />} />
        <Route path="/expenses/:id" element={<ExpenseDetail />} />
        <Route path="/expenses/:id/edit" element={<ExpenseForm />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/new" element={<InvoiceForm />} />
        <Route path="/invoices/:id" element={<InvoiceDetail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // ADMIN: full access
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/invoices" element={<Invoices />} />
      <Route path="/invoices/new" element={<InvoiceForm />} />
      <Route path="/invoices/:id" element={<InvoiceDetail />} />
      <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
      <Route path="/estimates" element={<Estimates />} />
      <Route path="/estimates/new" element={<EstimateForm />} />
      <Route path="/estimates/:id" element={<EstimateDetail />} />
      <Route path="/estimates/:id/edit" element={<EstimateForm />} />
      <Route path="/delivery-challans" element={<DeliveryChallans />} />
      <Route path="/delivery-challans/new" element={<DeliveryChallanForm />} />
      <Route path="/delivery-challans/:id" element={<DeliveryChallanDetail />} />
      <Route path="/delivery-challans/:id/edit" element={<DeliveryChallanForm />} />
      <Route path="/credit-notes" element={<CreditNotes />} />
      <Route path="/credit-notes/new" element={<CreditNoteForm />} />
      <Route path="/credit-notes/:id" element={<CreditNoteDetail />} />
      <Route path="/credit-notes/:id/edit" element={<CreditNoteForm />} />
      <Route path="/customers" element={<Customers />} />
      <Route path="/customers/new" element={<CustomerForm />} />
      <Route path="/customers/:id" element={<CustomerDetail />} />
      <Route path="/customers/:id/edit" element={<CustomerForm />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/new" element={<ProductForm />} />
      <Route path="/products/:id/edit" element={<ProductForm />} />
      <Route path="/payments" element={<Payments />} />
      <Route path="/expenses" element={<Expenses />} />
      <Route path="/expenses/new" element={<ExpenseForm />} />
      <Route path="/expenses/:id" element={<ExpenseDetail />} />
      <Route path="/expenses/:id/edit" element={<ExpenseForm />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/bank" element={<BankReconciliation />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <AuthRoute>
            <Layout>
              <AppRoutes />
            </Layout>
          </AuthRoute>
        }
      />
    </Routes>
  )
}
